import {
  Button,
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
  SidebarTrigger,
  cn,
  useSidebarOptional,
} from "@beweegbaas/ui";
import {
  Children,
  Fragment,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  isValidElement,
  type ComponentProps,
  type ReactNode,
} from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { m } from "#/paraglide/messages";
import { useActiveNavLabel } from "#/components/app-sidebar";
import { useActiveAdminNavLabel } from "#/modules/admin/components/admin-sidebar";
import {
  extractReactChildrenByTypeDeep,
  extractReactElementsByTypeDeep,
} from "#/modules/util.helpers/utils.react-children";
import IconMaterialSymbolsMoreHoriz from "~icons/material-symbols/more-horiz";

/**
 * Shadcn-style containers: a centered max-width with plain edge padding. The previous
 * viewport-math variants (`min(…, calc(100vw-…))`, center-by-padding) predate the sidebar
 * layout — `100vw` included the sidebar rail, so those calcs no longer measured the actual
 * content area. Full-bleed bars (Page.Header etc.) now wrap this in their own outer element.
 */
export const containerStyles = tv({
  base: "w-full min-w-0",
  variants: {
    center: {
      true: "mx-auto",
      false: "",
    },
    // Gutter + default max-width come from global CSS vars (`--container-gutter`,
    // `--container-max-width` in @beweegbaas/ui index.css) — tweak them there; edge-aligned
    // tables (`Table alignToContainer`) read the same vars.
    paddingContainer: {
      true: "px-(--container-gutter)",
      false: "",
    },
    container: {
      full: "max-w-none",
      default: "max-w-(--container-max-width)",
      sm: "max-w-5xl",
      xs: "max-w-3xl",
    },
  },
  compoundVariants: [
    {
      paddingContainer: true,
      container: "full",
      class: "px-0",
    },
  ],
  defaultVariants: {
    container: "default",
    paddingContainer: true,
    center: true,
  },
});

type ContainerVariantProps = VariantProps<typeof containerStyles>;

/** Root props for the new page shell. Extend with variants (e.g. tailwind-variants) as the design evolves. */
export interface PageProps extends ComponentProps<"section"> {
  container?: keyof typeof containerStyles.variants.container;
  size?: "default" | "sm" | "xs";
  center?: boolean;
}

const PageRootContext = createContext<PageProps>({
  container: "default",
  size: "default",
  center: true,
});

/**
 * True inside the mobile header-actions bottom drawer — lets `Page.Header.SecondaryActions`
 * children render their actual content there (e.g. a form) instead of a trigger button.
 */
const HeaderMobileActionsContext = createContext(false);

export function useInHeaderMobileActions() {
  return useContext(HeaderMobileActionsContext);
}

/** True inside `Page.Header` — lets `Page.Title` know it is mirrored into the mobile trigger row. */
const PageHeaderContext = createContext(false);

/** True only where `Page.Header` renders extracted `Page.Header.Section` elements — the inline copy in `children` renders nothing. */
const HeaderSectionSlotContext = createContext(false);

function PageRoot({
  className,
  children,
  container = "default",
  size = "default",
  center = true,
  ...props
}: PageProps) {
  return (
    <PageRootContext.Provider value={{ container, size, center }}>
      <section className={cn("flex min-h-0 min-w-0 flex-1 flex-col", className)} {...props}>
        {children}
      </section>
    </PageRootContext.Provider>
  );
}

interface HeaderProps extends ComponentProps<"header"> {
  sticky?: boolean;
  /** Classes for the full-bleed outer bar (border, background); `className` styles the inner container. */
  barClassName?: string;
}

/** Sentinel + IntersectionObserver: when the sentinel leaves the viewport top, the following sticky header is stuck. */
function useStickyHeaderStuckObserver(enabled: boolean) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsStuck(false);
      return;
    }
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setIsStuck(!entry.isIntersecting);
      },
      { root: null, rootMargin: "0px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  return { sentinelRef, isStuck };
}

/**
 * Slot markers that `Header` places itself. Anything else inside `Page.Header.Actions` is a "loose"
 * action — see `looseActions` in `Header`. Function declarations hoist, so listing them here is fine.
 */
const HEADER_ACTION_SLOTS = [MainAction, BackAction, SecondaryActions, FilterItems, SearchField];

function containsHeaderActionSlot(node: ReactNode): boolean {
  return HEADER_ACTION_SLOTS.some((slot) => extractReactChildrenByTypeDeep(node, slot).length > 0);
}

function Header({ className, barClassName, children, sticky = true, ...props }: HeaderProps) {
  const { container, center } = useContext(PageRootContext);
  const sidebar = useSidebarOptional();
  // School scope resolves via the school nav; admin scope (no $school param) via the admin nav.
  const activeNavLabel = useActiveNavLabel();
  const activeAdminNavLabel = useActiveAdminNavLabel();
  const { sentinelRef, isStuck } = useStickyHeaderStuckObserver(sticky);
  const searchField = extractReactChildrenByTypeDeep(children, SearchField);
  const filterItems = extractReactChildrenByTypeDeep(children, FilterItems);
  const sections = extractReactElementsByTypeDeep(children, HeaderSection);
  const mainAction = extractReactChildrenByTypeDeep(children, MainAction);
  const backAction = extractReactChildrenByTypeDeep(children, BackAction);
  // `Page.Header.Actions` renders in the wide layout only. Children dropped in there without a slot
  // marker used to vanish below the 620px threshold with nowhere to go (the "voeg een klas toe"
  // button, ticket 30's class of bug), so they default to being a main action: rendered inline by
  // `Actions` for the wide row, mirrored into the compact row here. A wrapper that itself holds a
  // slot is left alone — its slots are already placed.
  const looseActions = extractReactChildrenByTypeDeep(children, HeaderActions)
    .flatMap((node) => Children.toArray(node))
    .filter((child) => isValidElement(child) && !containsHeaderActionSlot(child));
  const secondaryActions = extractReactChildrenByTypeDeep(children, SecondaryActions);
  // The page's own title when it has one, else the active sidebar nav label — so the mobile
  // trigger row always has a title next to the toggle.
  const titleSlots = extractReactChildrenByTypeDeep(children, Title);
  const mobileTitle =
    titleSlots.length > 0 ? titleSlots[0] : (activeNavLabel ?? activeAdminNavLabel);

  return (
    <>
      {sticky ? (
        <div ref={sentinelRef} className="pointer-events-none h-px w-full shrink-0" aria-hidden />
      ) : null}
      <header
        data-stuck={isStuck ? "true" : undefined}
        className={cn(
          "w-full border-b",
          "md:data-stuck:border-b-1! md:data-stuck:backdrop-blur-sm!",
          `
          [&:has(+_.page-subnavigation)]:-mb-2
          [&:has(+_.page-subnavigation)]:border-b-0
          [&:has(+_.page-subnavigation)]:backdrop-blur-none
          `,
          {
            "top-0 z-20 md:sticky": sticky,
          },
          barClassName,
        )}
        {...props}
      >
        {/* Everything that switches between the compact and the wide header layout keys off
            `@container/page-header` (the header's own content width), never a viewport
            breakpoint: the sidebar rail eats ~256px, so on iPad portrait with the sidebar open
            a `md` viewport still leaves a ~500px header. Mixing the two systems left a dead
            zone (viewport >= md, container < 620px) where the compact row was hidden by `md:`
            and the wide row by the container query — the actions vanished entirely (ticket 30). */}
        <div className={cn(containerStyles({ container, center }), "@container/page-header")}>
          <div
            className={cn(
              `flex min-h-16 flex-col gap-4 py-4 @[620px]/page-header:flex-row
              @[620px]/page-header:flex-wrap @[620px]/page-header:items-center
              @[620px]/page-header:py-2`,
              "@max-[620px]/page-header:justify-center",
              className,
            )}
          >
            {/* Mobile: sidebar trigger + page title (the page's `Page.Title` hides itself
              on mobile via PageHeaderContext because it is mirrored here). Desktop toggle
              sits on `Page.Title`; pages without a title still get a trigger here. */}
            {sidebar != null && sidebar.isMobile ? (
              <div className="flex min-w-0 items-center gap-2">
                <SidebarTrigger
                  className="shrink-0 -ml-1.5"
                  aria-label={m["app.header.toggle_sidebar_aria"]()}
                />
                {mobileTitle != null && (
                  <h1 className="m-0 truncate text-lg font-bold">{mobileTitle}</h1>
                )}
              </div>
            ) : sidebar != null && titleSlots.length === 0 ? (
              <SidebarTrigger
                className="shrink-0 -ml-1.5"
                aria-label={
                  sidebar.state === "collapsed"
                    ? m["app.header.expand_sidebar_aria"]()
                    : m["app.header.collapse_sidebar_aria"]()
                }
              />
            ) : null}
            <PageHeaderContext.Provider value={true}>{children}</PageHeaderContext.Provider>

            {(searchField.length > 0 ||
              filterItems.length > 0 ||
              secondaryActions.length > 0 ||
              backAction.length > 0 ||
              looseActions.length > 0 ||
              mainAction.length > 0) && (
              <>
                {searchField.length > 0 && (
                  <div className="grow *:w-full! @[620px]/page-header:hidden">{searchField}</div>
                )}

                <div className="@[620px]/page-header:hidden flex items-center gap-2 empty:hidden">
                  {backAction.length > 0 && (
                    <div className="flex shrink-0 items-center gap-2">
                      {backAction.map((item) => item)}
                    </div>
                  )}
                  {(mainAction.length > 0 || looseActions.length > 0) && (
                    <div className="grow @[620px]/page-header:hidden">
                      <div className="flex items-center gap-2 @max-[620px]/page-header:[&>*]:w-full!">
                        {mainAction.map((item) => item)}
                        {looseActions.map((item) => item)}
                      </div>
                    </div>
                  )}
                  {(filterItems.length > 0 || secondaryActions.length > 0) && (
                    <div className="flex items-center gap-2">
                      <Drawer>
                        <DrawerTrigger
                          render={
                            <Button
                              type="button"
                              variant="secondary-light"
                              aria-label={m["app.header.more_actions_aria"]()}
                              nativeButton={true}
                            >
                              <IconMaterialSymbolsMoreHoriz />
                            </Button>
                          }
                        />
                        <DrawerContent>
                          <DrawerTitle className="sr-only">
                            {m["app.header.more_actions_aria"]()}
                          </DrawerTitle>
                          <div className={cn("flex flex-col items-stretch gap-4 p-4", className)}>
                            <HeaderMobileActionsContext.Provider value={true}>
                              {filterItems.length > 0 && (
                                <div className="flex items-center gap-2">
                                  {filterItems.map((item) => item)}
                                </div>
                              )}
                              {secondaryActions.length > 0 && secondaryActions.map((item) => item)}
                            </HeaderMobileActionsContext.Provider>
                          </div>
                        </DrawerContent>
                      </Drawer>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {sections.length > 0 && (
          <HeaderSectionSlotContext.Provider value={true}>
            {sections.map((section, index) => (
              <Fragment key={index}>{section}</Fragment>
            ))}
          </HeaderSectionSlotContext.Provider>
        )}
      </header>
    </>
  );
}

interface HeaderSectionProps extends ComponentProps<"div"> {
  /** Classes for the full-bleed outer bar (border, background); `className` styles the inner container. */
  barClassName?: string;
}

/**
 * Stacked full-bleed layer inside `Page.Header`, below the main header row (e.g. a filter bar).
 * Declare as a child of `Page.Header`; it is extracted and rendered as its own bar with a subtle
 * top divider — the header's bottom border closes it off. Multiple sections stack in order.
 */
function HeaderSection({ className, barClassName, children, ...props }: HeaderSectionProps) {
  const inSlot = useContext(HeaderSectionSlotContext);
  const { container, center } = useContext(PageRootContext);
  // Rendered inline via `children` too — only the extracted slot copy produces output.
  if (!inSlot) return null;
  return (
    <div className={cn("w-full border-t", barClassName)} {...props}>
      <div
        className={cn(
          containerStyles({ container, center }),
          "flex min-h-12 flex-wrap items-center gap-2 py-2",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
HeaderSection.displayName = "Page.Header.Section";

/**
 * Mobile-only header bar: just the sidebar trigger + a title. For pages/states without a full
 * `Page.Header` (empty states, content pages) — keeps the sidebar reachable on mobile while
 * desktop stays header-less. Pass a `Page.Title` child for pages outside the sidebar nav;
 * without children the title falls back to the active nav label.
 */
function MobileHeader({ children }: { children?: ReactNode }) {
  return (
    <Header sticky={false} barClassName="md:hidden">
      {children}
    </Header>
  );
}

export type PageHeaderActionsProps = Omit<ComponentProps<"div">, "children"> & {
  children?: ReactNode;
};

/**
 * Wide-layout actions row. Hidden below 620px of header width, where `Page.Header` renders the
 * compact row (main action + a drawer for filters/secondary actions) instead. Both sides use the
 * same container-query threshold so exactly one of them renders — see the note in `Header`.
 */
function HeaderActions({ className, children, ...props }: PageHeaderActionsProps) {
  return (
    <>
      <div
        className={cn(
          "ml-auto hidden min-w-0 flex-wrap items-center justify-end gap-2 @[620px]/page-header:flex",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </>
  );
}

/** Standalone page heading (e.g. auth flows, simple content pages). Not the same as the sticky `Page.Header` chrome. */
function Title({ className, children, ...props }: ComponentProps<"h1">) {
  // Inside a Page.Header with a sidebar the title is mirrored into the mobile trigger row,
  // so the original only shows from md up. On desktop the toggle sits to the left of this title.
  const inHeader = useContext(PageHeaderContext);
  const sidebar = useSidebarOptional();
  const heading = (
    <h1 className={cn("m-0 text-lg font-bold", className)} {...props}>
      {children}
    </h1>
  );

  if (!inHeader || sidebar == null) {
    return heading;
  }

  return (
    <div className="flex min-w-0 items-center gap-2 max-md:hidden">
      {sidebar.isMobile ? null : (
        <SidebarTrigger
          className="shrink-0 -ml-1.5"
          aria-label={
            sidebar.state === "collapsed"
              ? m["app.header.expand_sidebar_aria"]()
              : m["app.header.collapse_sidebar_aria"]()
          }
        />
      )}
      {heading}
    </div>
  );
}
Title.displayName = "Page.Title";

function Body({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("mt-6 min-h-0 flex-1 pb-6", className)} {...props}>
      {children}
    </div>
  );
}

interface ContainerProps extends ComponentProps<"div">, ContainerVariantProps {}
function Container({
  className,
  children,
  container,
  center,
  paddingContainer: _paddingContainer,
  ...props
}: ContainerProps) {
  const { size, container: containerContext, center: centerContext } = useContext(PageRootContext);

  container = containerContext ?? container;
  center = centerContext ?? center;

  if (!center) {
    return (
      <div className={cn(containerStyles({ container: "default" }), className)}>
        <div
          className={cn(
            containerStyles({ container, paddingContainer: false, center }),
            { "max-w-5xl": size === "sm" },
            { "max-w-3xl": size === "xs" },
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        containerStyles({ container, center }),
        { "max-w-5xl": size === "sm" },
        { "max-w-3xl": size === "xs" },
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface TopHeaderProps extends ComponentProps<"div"> {
  /** Classes for the full-bleed outer bar (border, background); `className` styles the inner container. */
  barClassName?: string;
}

function TopHeader({ className, barClassName, children, ...props }: TopHeaderProps) {
  const { center, container } = useContext(PageRootContext);
  return (
    <div className={cn("w-full border-b", barClassName)} {...props}>
      <div className={cn(containerStyles({ container, center }), "py-3", className)}>
        {children}
      </div>
    </div>
  );
}

/* Sidebar layouts respond to the CONTENT width (the app sidebar rail eats ~256px of the
   viewport), so the row/stack switch and aside widths are container queries against the
   `sidebar-layout` container, not viewport breakpoints. */
function SidebarLayout({ className, children, ...props }: ComponentProps<"section">) {
  return (
    <div className="@container/sidebar-layout flex grow flex-col *:grow">
      <section
        className={cn(
          `flex grow flex-col gap-x-10 *:grow @4xl/sidebar-layout:flex-row
          [&>*]:min-w-[0px]`,
          className,
        )}
        {...props}
      >
        {children}
      </section>
    </div>
  );
}

function Sidebar({ className, children, ...props }: ComponentProps<"aside">) {
  const { size } = useContext(PageRootContext);
  return (
    <aside
      className={cn(
        `page-sidebar w-full shrink-0 @4xl/sidebar-layout:max-w-[260px]`,
        {
          "@7xl/sidebar-layout:max-w-[320px]": size === "default",
        },
        className,
      )}
      {...props}
    >
      {children}
    </aside>
  );
}

interface FocusedLayoutProps extends ComponentProps<"div"> {
  align?: "start" | "center";
}

/** Narrow single-column page body (xs container) for focused flows like the import upload page. */
function FocusedLayout({ align = "start", className, children, ...props }: FocusedLayoutProps) {
  return (
    <div
      className={cn(
        containerStyles({ container: "sm", center: align === "center" }),
        "pb-10",
        className,
      )}
      {...props}
    >
      <div className={cn("flex flex-col gap-6", align === "center" && "items-center text-center")}>
        {children}
      </div>
    </div>
  );
}

function Subnavigation({ className, children, ...props }: ComponentProps<"div">) {
  const { center, container } = useContext(PageRootContext);
  return (
    <div className="page-subnavigation w-full min-w-0 border-b" {...props}>
      <div className={cn(containerStyles({ container, center }), "pt-3", className)}>
        {children}
      </div>
    </div>
  );
}

function FilterItems({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("", className)} {...props}>
      {children}
    </div>
  );
}

/**
 * Step-back link for wizard headers. Like `MainAction` it renders in both header layouts — inline
 * here for the wide row, duplicated by `Header` into the compact row — so it never disappears the
 * way a viewport-guarded (`max-lg:hidden`) bare child did.
 */
function BackAction({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("hidden @[620px]/page-header:block", className)} {...props}>
      {children}
    </div>
  );
}

function MainAction({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("hidden @[620px]/page-header:block", className)} {...props}>
      {children}
    </div>
  );
}

function SecondaryActions({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("hidden @[620px]/page-header:block", className)} {...props}>
      {children}
    </div>
  );
}

function SearchField({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("hidden @[620px]/page-header:block", className)} {...props}>
      {children}
    </div>
  );
}

/** Stable across HMR; must match what `extractReactChildrenByType*` compares via `displayName`. */
const PAGE_HEADER_SLOT_NAME = {
  Actions: "Page.Header.Actions",
  FilterItems: "Page.Header.FilterItems",
  BackAction: "Page.Header.BackAction",
  MainAction: "Page.Header.MainAction",
  SearchField: "Page.Header.SearchField",
  SecondaryActions: "Page.Header.SecondaryActions",
} as const;

HeaderActions.displayName = PAGE_HEADER_SLOT_NAME.Actions;
BackAction.displayName = PAGE_HEADER_SLOT_NAME.BackAction;
FilterItems.displayName = PAGE_HEADER_SLOT_NAME.FilterItems;
MainAction.displayName = PAGE_HEADER_SLOT_NAME.MainAction;
SearchField.displayName = PAGE_HEADER_SLOT_NAME.SearchField;
SecondaryActions.displayName = PAGE_HEADER_SLOT_NAME.SecondaryActions;

const PageHeader = Object.assign(Header, {
  FilterItems: FilterItems,
  BackAction: BackAction,
  MainAction: MainAction,
  Actions: HeaderActions,
  SearchField: SearchField,
  SecondaryActions: SecondaryActions,
  Section: HeaderSection,
});

PageRoot.Header = PageHeader;
PageRoot.MobileHeader = MobileHeader;
PageRoot.HeaderActions = HeaderActions;
PageRoot.Title = Title;
PageRoot.Body = Body;
PageRoot.TopHeader = TopHeader;
PageRoot.SidebarLayout = SidebarLayout;
PageRoot.FocusedLayout = FocusedLayout;
PageRoot.Sidebar = Sidebar;
PageRoot.Container = Container;
PageRoot.Subnavigation = Subnavigation;
/** New page layout primitive. Routes that still use the old layout should import `Page` from `#/components/page`. */
export const Page = PageRoot;
